import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import { PrismCode } from 'react-prism';
import remarkGfm from 'remark-gfm';
import type { Message } from '@dev-streetbeat/chat/types';
import styles from './../../chat.module.scss';

import React from 'react';
import rehypeKatex from 'rehype-katex';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import colorsStore from 'shared/store/colorsStore';
import { preprocessLaTeX } from 'utils/textUtils';
import { TableWrapper } from './TableWrapper';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { SANITIZE_SCHEMA, safeUrl } from 'utils/safeMarkdown';
import { replyLinkTarget } from 'utils/replyLink';
import { ChatAgentCheckIcon } from 'shared/assets/svg/ChatAgentCheckIcon';
import { useFileOperations, useFiles } from '@dev-streetbeat/chat/react';

type Props = {
  message: Message | string;
  onPortfolio?: boolean;
  compact?: boolean;
};

export const TextResponse: React.FC<Props> = ({ message, onPortfolio = false, compact = false }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const firstTextBlock =
    typeof message === 'string'
      ? undefined
      : (message?.content?.find((b) => b.type === 'text') as { type: 'text'; text: { value: string } } | undefined);
  const textContent =
    typeof message === 'string'
      ? message
      : firstTextBlock?.text?.value;

  const { files } = useFiles();
  const { downloadFile } = useFileOperations();

  const latexSafeContent = preprocessLaTeX(textContent || '');

  const customRenderers = {
    table: ({ children }) => <TableWrapper>{children}</TableWrapper>,
    a: ({ children, ...props }) => {
      const handleClick = async () => {
        const target = replyLinkTarget(props.href, files);
        if (!target) return;
        if ('url' in target) {
          window.open(target.url, '_blank', 'noopener,noreferrer');
          return;
        }

        const response = await downloadFile(target.file.id);

        const link = document.createElement('a');
        link.href = URL.createObjectURL(response);
        link.download = target.file.name;
        link.click();
      };

      return (
        <button className={styles.chatACopy} {...props} onClick={handleClick}>
          {children}
        </button>
      );
    },
    pre(props: any) {
      const className = props.node.children?.[0]?.properties?.className?.[0];
      return (
        <PrismCode
          component="pre"
          className={`rounded-xl font-sans text-[12px] !text-FinecoBlack shadow-none ${className}`}
          style={{ 
            textShadow: 'none',
            maxWidth: '100%',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word'
          }}
          {...props.attributes}
        >
          {props.children}
        </PrismCode>
      );
    },
    checkicon: () => (
      <span style={{ display: 'inline-flex', marginRight: 6 }}>
        <ChatAgentCheckIcon />
        <div>ciao ciao</div>
      </span>
    ),
    img(props: any) {
      return (
        <div className="my-8 flex justify-center">
          <img alt={props.alt || ''} {...props} />
        </div>
      );
    },
    p: ({ children, ...props }) => {
      return (
        <p className={classNames(styles.chatPCopy, compact && styles.chatPCopyCompact)} {...props}>
          {children}
        </p>
      );
    },
    li: ({ children, ...props }) => {
      return (
        <li style={{ color: colorsConfig.chatCopyColor }} {...props}>
          {children}
        </li>
      );
    },
    // eslint-disable-next-line jsx-a11y/heading-has-content -- children come through the ReactMarkdown-supplied ...props spread, always present for a heading AST node
    h1: ({ node, ...props }) => <h1 style={{ color: colorsConfig.chatCopyColor }} {...props} />,
    // eslint-disable-next-line jsx-a11y/heading-has-content -- children come through the ReactMarkdown-supplied ...props spread, always present for a heading AST node
    h2: ({ node, ...props }) => <h2 style={{ color: colorsConfig.chatCopyColor }} {...props} />,
    // eslint-disable-next-line jsx-a11y/heading-has-content -- children come through the ReactMarkdown-supplied ...props spread, always present for a heading AST node
    h3: ({ node, ...props }) => <h3 style={{ color: colorsConfig.chatCopyColor }} {...props} />,
    strong: ({ node, ...props }) => <strong style={{ color: colorsConfig.chatCopyColor }} {...props} />,
  };

  return (
    <div className={!onPortfolio ? styles.reactMarkdownContainer : styles.reactMarkdownContainerOnPortfolio}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
        remarkRehypeOptions={{ clobberPrefix: '' }}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, SANITIZE_SCHEMA], rehypeKatex]}
        urlTransform={safeUrl}
        components={customRenderers}
        className={classNames(styles.reactMarkdown, styles.chatMainCopy)}
      >
        {latexSafeContent}
      </ReactMarkdown>
    </div>
  );
};
